import { isValidBoardSlug } from "@/config/mixLineup";
import type { AddedBoard } from "@/features/settings/stores/mixStore";
import {
  createMediator,
  type Mediator,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";

export type BoardAdditionEvent =
  | { readonly type: "board-addition/draft-changed"; readonly value: string }
  | { readonly type: "board-addition/add-requested" }
  | { readonly type: "board-addition/remove-requested"; readonly slug: string }
  | {
      readonly type: "board-addition/completed";
      readonly requestId: number;
      readonly board: AddedBoard;
    }
  | {
      readonly type: "board-addition/failed";
      readonly requestId: number;
      readonly message: string;
    };

export type BoardAdditionState = Readonly<{
  draft: string;
  pending: boolean;
  requestId: number;
  error: string;
  message: string;
}>;

export type BoardAdditionEffect =
  | {
      readonly type: "validate";
      readonly slug: string;
      readonly requestId: number;
    }
  | { readonly type: "add"; readonly board: AddedBoard }
  | { readonly type: "remove"; readonly slug: string };

export function isBoardAdditionEvent(
  event: TypedEvent,
): event is BoardAdditionEvent {
  return [
    "board-addition/draft-changed",
    "board-addition/add-requested",
    "board-addition/remove-requested",
    "board-addition/completed",
    "board-addition/failed",
  ].includes(event.type);
}

/** 二重送信、重複、古い非同期完了を裁定して、検証成功後だけ保存する。 */
export function createBoardAdditionMediator(
  getExistingSlugs: () => readonly string[],
  runEffect: (effect: BoardAdditionEffect) => void,
  getAdditionError: (slug: string) => string = () => "",
): Mediator<BoardAdditionState, BoardAdditionEvent, BoardAdditionEffect> {
  return createMediator<
    BoardAdditionState,
    BoardAdditionEvent,
    BoardAdditionEffect
  >(
    { draft: "", pending: false, requestId: 0, error: "", message: "" },
    (state, event) => {
      switch (event.type) {
        case "board-addition/draft-changed":
          return {
            status: "handled",
            state: state.pending
              ? state
              : { ...state, draft: event.value, error: "", message: "" },
          };
        case "board-addition/add-requested": {
          if (state.pending) return { status: "handled", state };
          const slug = state.draft.trim();
          const error = !isValidBoardSlug(slug)
            ? "板slugは半角小文字・数字・ハイフンで1〜32文字にしてください"
            : getExistingSlugs().includes(slug)
              ? "この板はすでに候補にあります"
              : "";
          if (error)
            return {
              status: "handled",
              state: { ...state, error, message: "" },
            };
          const requestId = state.requestId + 1;
          return {
            status: "handled",
            state: {
              ...state,
              pending: true,
              requestId,
              error: "",
              message: "",
            },
            effects: [{ type: "validate", slug, requestId }],
          };
        }
        case "board-addition/remove-requested":
          return {
            status: "handled",
            state: {
              ...state,
              pending: false,
              requestId: state.requestId + 1,
              message: "追加を解除しました",
              error: "",
            },
            effects: [{ type: "remove", slug: event.slug }],
          };
        case "board-addition/completed": {
          if (!state.pending || state.requestId !== event.requestId)
            return { status: "handled", state };
          if (getExistingSlugs().includes(event.board.slug))
            return {
              status: "handled",
              state: {
                ...state,
                pending: false,
                error: "この板はすでに候補にあります",
              },
            };
          const additionError = getAdditionError(event.board.slug);
          if (additionError)
            return {
              status: "handled",
              state: {
                ...state,
                pending: false,
                error: additionError,
                message: "",
              },
            };
          return {
            status: "handled",
            state: {
              ...state,
              pending: false,
              draft: "",
              message: `${event.board.boardName}を追加しました`,
            },
            effects: [{ type: "add", board: event.board }],
          };
        }
        case "board-addition/failed":
          return {
            status: "handled",
            state:
              !state.pending || state.requestId !== event.requestId
                ? state
                : { ...state, pending: false, error: event.message },
          };
      }
    },
    { runEffect },
  );
}
